/**
 * @file index.tsx
 *
 * @fileoverview Renders appropriate job detail description based on the job type.
 */

import React, { useContext } from 'react';
import { JobDetailBodyProps } from '~/jobiqo/components/Job/Detail/components/Body';
import JobTypeEnum from '~/jobiqo/enums/jobType';
import { getIframeCustomParams } from '~/jobiqo/components/Job/Detail/utils';
import Link from '~/jobiqo/components/Link';
import OriginalAdButton from '~/jobiqo/components/Job/Detail/components/OriginalAdButton';
import styles from './jobDescription.module.scss';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { HideDesktop } from '~/vcl/atoms/06-layout/02-visibility/01-hide-desktop';
import getIframeClientCustomParams from '~/jobiqo/components/Slots/JobDescriptionIframeSlot';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import {
  normalizeSiteOrigin,
  transformExternalAnchors,
} from '~/jobiqo/lib/html/externalAnchors';

interface JobDescriptionProps {
  description?: string | null;
  noFollowExternalLinks?: boolean;
  plainTextExternalLinks?: boolean;
  currentHost?: string | null;
}

function JobDescription({
  description,
  noFollowExternalLinks = false,
  plainTextExternalLinks = false,
  currentHost,
}: JobDescriptionProps) {
  const siteOrigin =
    normalizeSiteOrigin(process.env.CLIENT_URL) ??
    normalizeSiteOrigin(currentHost);
  const content = transformExternalAnchors(description ?? '', siteOrigin, {
    addNoFollow: noFollowExternalLinks,
    convertToPlainText: plainTextExternalLinks,
  });

  return <WYSIWYGText format="full_html" content={content} />;
}
function getIframeSrc(src: string): string {
  // Append PDF open parameters to hide the navigation pane (sidebar),
  // hide the toolbar, and set default zoom to 100%
  // in the browser's built-in PDF viewer.
  if (src.toLowerCase().endsWith('.pdf')) {
    return `${src}#navpanes=0&toolbar=0&zoom=100`;
  }
  return src;
}

function getMobileIframeSrc(src: string): string {
  // On mobile, fit the PDF to the iframe width for better readability
  // and hide the toolbar to save screen space.
  if (src.toLowerCase().endsWith('.pdf')) {
    return `${src}#navpanes=0&toolbar=0&view=FitH`;
  }
  return src;
}

function IFrame({ src, dimensions, job, isMobile = false }) {
  return (
    <iframe
      className={isMobile ? styles.mobileIframe : undefined}
      src={isMobile ? getMobileIframeSrc(src) : getIframeSrc(src)}
      height={isMobile ? '100%' : dimensions.height}
      width={
        isMobile
          ? '100%'
          : typeof dimensions.width === 'number'
          ? dimensions.width - 50
          : dimensions.width
      }
      {...getIframeCustomParams()}
      {...getIframeClientCustomParams(src, job)}
    />
  );
}
/**
 * Renders description section, depending on the isIframe param,
 * renders an iframe or description text with the original job ad button.
 *
 */
function JobDescriptionWithSrc({
  src,
  isIframe,
  description,
  dimensions,
  job,
  noFollowExternalLinks = false,
  plainTextExternalLinks = false,
}) {
  const { JOBS } = useFrontendConfig();
  const FORCE_IFRAME = JOBS.JOB_DESCRIPTION_FORCE_IFRAME;
  const { host }: PathContextInterface = useContext(PathContext);

  const Description = (
    <FlexItems flexDirection="column" gap={8} alignItems={['center']}>
      <FlexItem>
        <JobDescription
          description={description}
          noFollowExternalLinks={noFollowExternalLinks}
          plainTextExternalLinks={plainTextExternalLinks}
          currentHost={host}
        />
      </FlexItem>
      <FlexItem marginBottom={4}>
        <Link asChild href={src}>
          <OriginalAdButton job={job} src={src} />
        </Link>
      </FlexItem>
    </FlexItems>
  );

  return (
    <>
      <HideMobile className={styles.fullHeight}>
        {isIframe ? (
          <IFrame src={src} dimensions={dimensions} job={job} />
        ) : (
          Description
        )}
      </HideMobile>
      <HideDesktop>
        {FORCE_IFRAME ? (
          <div className={styles.mobileIframeContainer}>
            <IFrame src={src} dimensions={dimensions} job={job} isMobile />
          </div>
        ) : description ? (
          Description
        ) : (
          <div className={styles.mobileIframeContainer}>
            <IFrame src={src} dimensions={dimensions} job={job} isMobile />
          </div>
        )}
      </HideDesktop>
    </>
  );
}

export const JobDetailBodyDescription = ({
  job,
  dimensions,
  isIframe,
  noFollowExternalLinks = false,
  plainTextExternalLinks = false,
}: JobDetailBodyProps) => {
  const { host }: PathContextInterface = useContext(PathContext);

  // Renders appropriate description for each job type.
  const JobDescriptionPerTypeMap = {
    [JobTypeEnum.JOB_PER_TEMPLATE]: (
      <JobDescription
        description={job.description}
        noFollowExternalLinks={noFollowExternalLinks}
        plainTextExternalLinks={plainTextExternalLinks}
        currentHost={host}
      />
    ),
    [JobTypeEnum.JOB_PER_BACKFILL]: (
      <JobDescription
        description={job.description}
        noFollowExternalLinks={noFollowExternalLinks}
        plainTextExternalLinks={plainTextExternalLinks}
        currentHost={host}
      />
    ),
    [JobTypeEnum.JOB_PER_LINK]: job.linkUrl && (
      <JobDescriptionWithSrc
        src={job.linkUrl}
        isIframe={isIframe}
        description={job.description}
        dimensions={dimensions}
        job={job}
        noFollowExternalLinks={noFollowExternalLinks}
        plainTextExternalLinks={plainTextExternalLinks}
      />
    ),
    [JobTypeEnum.JOB_PER_FILE]: job.file && (
      <JobDescriptionWithSrc
        src={job.file}
        isIframe={isIframe}
        description={job.description}
        dimensions={dimensions}
        job={job}
        noFollowExternalLinks={noFollowExternalLinks}
        plainTextExternalLinks={plainTextExternalLinks}
      />
    ),
  };

  return JobDescriptionPerTypeMap[job.type];
};

export default JobDetailBodyDescription;
